// ---------------------------------------------------------------------------
// AnnouncementBanner — In-app notification banner
//
// Displays announcements fetched from the UITL backend. Shows a badge with
// the unread count that expands to a list of announcements on click.
//
// Features:
//   - Floating badge with unread count (or inline banner)
//   - Click badge to expand/collapse announcement list
//   - Type badges with emoji icons (feature, fix, maintenance, info)
//   - Mark as read on click
//   - Dismiss individual announcements
//   - All inline styles, no external CSS
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from 'react';
import type { Announcement } from '../types';
import { useUITL } from './UITLProvider';

export interface AnnouncementBannerProps {
  /** Render as a fixed banner at the top of the page. Defaults to false. */
  fixed?: boolean;
  /** Maximum number of announcements to display. Defaults to 10. */
  max?: number;
  /** Position when using floating badge mode. Defaults to 'top-right'. */
  badgePosition?: 'top-right' | 'top-left';
}

const TYPE_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  feature: { icon: '🎉', label: 'Feature', color: '#16A34A' },
  fix: { icon: '🔧', label: 'Fix', color: '#2563EB' },
  maintenance: { icon: '⚠️', label: 'Maintenance', color: '#D97706' },
  info: { icon: 'ℹ️', label: 'Info', color: '#6366F1' },
};

function getTypeConfig(type: string) {
  return TYPE_CONFIG[type] ?? TYPE_CONFIG.info;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export function AnnouncementBanner({
  fixed = false,
  max = 10,
  badgePosition = 'top-right',
}: AnnouncementBannerProps) {
  const { client, config, remoteConfig, theme: t } = useUITL();

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [readIds, setReadIds] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch announcements on mount
  useEffect(() => {
    client
      .getAnnouncements()
      .then((items) => {
        setAnnouncements(items.slice(0, max));
        setLoading(false);
      })
      .catch((err) => {
        console.error('[UITL SDK] Failed to load announcements:', err);
        setLoading(false);
      });
  }, [client, max]);

  const markAsRead = useCallback(
    (id: number) => {
      if (readIds.has(id)) return;
      setReadIds((prev) => new Set(prev).add(id));
      client.markAnnouncementRead(id).catch(() => {});
    },
    [client, readIds],
  );

  const dismiss = useCallback(
    (id: number) => {
      setDismissed((prev) => new Set(prev).add(id));
      markAsRead(id);
    },
    [markAsRead],
  );

  const visible = announcements.filter((a) => !dismissed.has(a.id));
  const unreadCount = visible.filter((a) => !readIds.has(a.id)).length;

  if (loading || visible.length === 0) return null;

  const isRight = badgePosition === 'top-right';

  // ────────────────────────────────────────────────────────────
  // Fixed banner mode: renders at the top of the page
  // ────────────────────────────────────────────────────────────
  if (fixed) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: t.zIndex + 7,
          fontFamily: t.fontFamily,
        }}
      >
        {visible.map((a) => {
          const tc = getTypeConfig(a.announcement_type);
          return (
            <div
              key={a.id}
              onClick={() => markAsRead(a.id)}
              style={{
                background: tc.color,
                color: '#fff',
                padding: '10px 18px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 14,
                cursor: 'default',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <span>{tc.icon}</span>
                <strong>{a.title}</strong>
                {a.body && (
                  <span style={{ opacity: 0.9, marginLeft: 4 }}>
                    {' -- '}
                    {a.body.length > 120 ? a.body.slice(0, 120) + '...' : a.body}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dismiss(a.id);
                }}
                aria-label="Dismiss announcement"
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 14,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: 12,
                  flexShrink: 0,
                }}
              >
                &#x2715;
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  // Floating badge mode: badge with unread count + dropdown list
  // ────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        [isRight ? 'right' : 'left']: 16,
        zIndex: t.zIndex + 7,
        fontFamily: t.fontFamily,
      }}
    >
      {/* Badge button */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        aria-label={`${unreadCount} unread announcements`}
        style={{
          background: t.primaryColor,
          color: '#fff',
          border: 'none',
          borderRadius: t.borderRadius,
          padding: '8px 14px',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: t.fontFamily,
          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          position: 'relative',
        }}
      >
        <span>📢</span>
        <span>Announcements</span>
        {unreadCount > 0 && (
          <span
            style={{
              background: t.errorColor,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 5px',
              marginLeft: 2,
            }}
          >
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown list */}
      {expanded && (
        <div
          style={{
            marginTop: 8,
            width: 360,
            maxHeight: 420,
            overflowY: 'auto',
            background: t.backgroundColor,
            borderRadius: t.borderRadius + 2,
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            border: `1px solid ${t.borderColor}`,
          }}
        >
          {visible.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: t.textSecondary,
                fontSize: 14,
              }}
            >
              No announcements
            </div>
          ) : (
            visible.map((a, i) => {
              const tc = getTypeConfig(a.announcement_type);
              const isRead = readIds.has(a.id);
              return (
                <div
                  key={a.id}
                  onClick={() => markAsRead(a.id)}
                  style={{
                    padding: '14px 16px',
                    borderBottom:
                      i < visible.length - 1
                        ? `1px solid ${t.borderColor}`
                        : 'none',
                    cursor: 'default',
                    background: isRead ? t.backgroundColor : t.surfaceColor,
                    transition: 'background 0.15s',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flex: 1,
                      }}
                    >
                      {/* Type badge */}
                      <span
                        style={{
                          background: tc.color,
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 4,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tc.icon} {tc.label}
                      </span>
                      {!isRead && (
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: t.primaryColor,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dismiss(a.id);
                      }}
                      aria-label="Dismiss"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: t.textSecondary,
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: '0 2px',
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      &#x2715;
                    </button>
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: t.textColor,
                      marginBottom: 4,
                    }}
                  >
                    {a.title}
                  </div>
                  {a.body && (
                    <div
                      style={{
                        fontSize: 13,
                        color: t.textSecondary,
                        lineHeight: 1.5,
                        marginBottom: 4,
                      }}
                    >
                      {a.body.length > 200
                        ? a.body.slice(0, 200) + '...'
                        : a.body}
                    </div>
                  )}
                  {a.published_at && (
                    <div
                      style={{
                        fontSize: 11,
                        color: t.textSecondary,
                        opacity: 0.7,
                      }}
                    >
                      {formatDate(a.published_at)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
